"use server";

import { auth } from "@/auth";
import { prisma } from "@/lib/prisma";
import { UserRole } from "@/generated/prisma/enums";
import { scheduleMagazineThumbnail } from "@/lib/generate-magazine-thumbnail";
import { revalidatePath } from "next/cache";
import { redirect } from "next/navigation";

async function requireAdmin() {
  const session = await auth();
  if (!session?.user || session.user.role !== UserRole.ADMIN) {
    throw new Error("Forbidden");
  }
  return session;
}

export async function createMagazineFromUpload(input: {
  title: string;
  category: string;
  storageKey: string;
  issue?: string;
  year?: number;
  month?: number;
  description?: string;
}): Promise<{ id: string }> {
  await requireAdmin();

  const title = input.title.trim();
  const category = input.category.trim();
  const storageKey = input.storageKey.trim();
  if (!title || !category || !storageKey) {
    throw new Error("Title, category, and storage key are required.");
  }

  if (input.month != null && (input.month < 1 || input.month > 12)) {
    throw new Error("Month must be between 1 and 12.");
  }

  const magazine = await prisma.magazine.create({
    data: {
      title,
      category,
      storageKey,
      description: input.description?.trim() || undefined,
      issue: input.issue?.trim() || undefined,
      year: input.year,
      month: input.month,
    },
  });

  revalidatePath("/magazines");
  revalidatePath("/admin/magazines");
  scheduleMagazineThumbnail(magazine.id);
  return { id: magazine.id };
}

export async function createMagazine(formData: FormData) {
  await requireAdmin();

  const title = String(formData.get("title") ?? "").trim();
  const category = String(formData.get("category") ?? "").trim();
  const storageKey = String(formData.get("storageKey") ?? "").trim();
  if (!title || !category || !storageKey) {
    throw new Error("Title, category, and storage key are required.");
  }

  const description = optionalString(formData.get("description"));
  const issue = optionalString(formData.get("issue"));
  const year = optionalInt(formData.get("year"));
  const month = optionalInt(formData.get("month"));
  if (month != null && (month < 1 || month > 12)) {
    throw new Error("Month must be between 1 and 12.");
  }

  const magazine = await prisma.magazine.create({
    data: {
      title,
      category,
      storageKey,
      description,
      issue,
      year,
      month,
    },
  });

  revalidatePath("/magazines");
  revalidatePath("/admin/magazines");
  scheduleMagazineThumbnail(magazine.id);
  redirect("/admin/magazines");
}

export async function updateMagazine(magazineId: string, formData: FormData) {
  await requireAdmin();

  const title = String(formData.get("title") ?? "").trim();
  const category = String(formData.get("category") ?? "").trim();
  const storageKey = String(formData.get("storageKey") ?? "").trim();
  if (!title || !category || !storageKey) {
    throw new Error("Title, category, and storage key are required.");
  }

  const month = optionalInt(formData.get("month"));
  if (month != null && (month < 1 || month > 12)) {
    throw new Error("Month must be between 1 and 12.");
  }

  await prisma.magazine.update({
    where: { id: magazineId },
    data: {
      title,
      category,
      storageKey,
      description: optionalString(formData.get("description")),
      issue: optionalString(formData.get("issue")),
      year: optionalInt(formData.get("year")),
      month,
    },
  });

  revalidatePath("/magazines");
  revalidatePath("/admin/magazines");
  revalidatePath(`/magazines/${magazineId}`);
  redirect("/admin/magazines");
}

export async function deleteMagazine(magazineId: string) {
  await requireAdmin();
  await prisma.magazine.delete({ where: { id: magazineId } });
  revalidatePath("/magazines");
  revalidatePath("/admin/magazines");
  redirect("/admin/magazines");
}

function optionalString(value: FormDataEntryValue | null): string | undefined {
  if (value == null) return undefined;
  const s = String(value).trim();
  return s.length ? s : undefined;
}

function optionalInt(value: FormDataEntryValue | null): number | undefined {
  if (value == null) return undefined;
  const s = String(value).trim();
  if (!s.length) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : undefined;
}
